import MoreVertIcon from '@mui/icons-material/MoreVert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { memo } from 'react'

import CandleChartBox from '@/components/common/CandleChartBox'
import useTrans from '@/hooks/useTrans'
import { format } from '@/utils/numeric'

import { ACTION_TYPE, COLUMNS_KEY, getColorStyle } from '../helpers'
import ViewTokenName from '../view.token-name'
import { ViewProps } from './types'
import ViewEmpty from './view.empty'
import ViewLoading from './view.loading'

const View = ({
  isLoading,
  columnAction,
  topCoinsData,
  onOpenMoreMenu,
  onCloseMoreMenu,
  currentTargetEl,
  tokenInChart,
  setTokenAddressInChart,
  onOpenCandleChart,
  onClickTokenItem,
}: ViewProps) => {
  const trans = useTrans()

  const isOpen = Boolean(currentTargetEl)
  const popoverId = isOpen ? 'three-dots-popover' : undefined
  const tokenMinForChart = currentTargetEl?.getAttribute('data-token-mint-for-chart') || ''

  return (
    <Stack gap="8px">
      {isLoading && <ViewLoading />}
      {!isLoading && topCoinsData.length === 0 && <ViewEmpty />}
      {!isLoading &&
        topCoinsData.map((data) => {
          return (
            <Stack
              key={data.id}
              className="cursor-pointer rounded-lg bg-[#1F2A3A] p-4"
              gap="12px"
              onClick={() => onClickTokenItem(data)}
              data-testid={`top-coins-card-mobile-${data.symbol}`}
            >
              <Stack direction="row" justifyContent="space-between">
                <ViewTokenName data={data} />
                {/* Should hide this icon because only have one menu item (Open Chart) in Popover for now */}
                {data.isHasChart && (
                  <Button
                    aria-describedby={popoverId}
                    data-token-mint-for-chart={data.isHasChart ? data.tokenMint : undefined}
                    onClick={(evt) => {
                      evt.stopPropagation()
                      onOpenMoreMenu(evt)
                    }}
                    className="rounded-lg p-0 w-[24px] h-[24px] min-w-0"
                  >
                    <MoreVertIcon className="w-[24px] h-[24px]" />
                  </Button>
                )}
              </Stack>

              <Stack direction="row" gap="12px">
                <Stack className="flex-1" gap="2px">
                  <Typography className="text-[#9CA3AE] text-xs">
                    {trans.core_tokens_list.table_headers.balance}
                  </Typography>
                  <Typography className="text-base font-bold">{data.userBalance}</Typography>
                </Stack>

                <Stack className="flex-1" gap="2px">
                  <Typography className="text-[#9CA3AE] text-xs">24h %</Typography>
                  <Typography
                    className="text-base font-bold"
                    style={getColorStyle(data.diff24Hours)}
                  >
                    {data.diff24Hours ? format(data.diff24Hours, { sign: true }) : 0}%
                  </Typography>
                </Stack>
              </Stack>

              {columnAction?.key === COLUMNS_KEY.actionsSpot ? (
                <Stack direction="row" gap="12px">
                  <Button
                    data-testid={`${data.symbol}-withdraw-btn`}
                    onClick={(evt) => {
                      evt.stopPropagation()
                      columnAction?.onClick?.(data, ACTION_TYPE.withdraw)
                    }}
                    className="flex-1 capitalize text-xs font-semibold rounded-lg p-2 min-w-[90px] border border-solid border-[#61667E] text-[#C2CBD9]"
                  >
                    {trans.common.withdraw}
                  </Button>
                  <Button
                    data-testid={`${data.symbol}-deposit-btn`}
                    onClick={(evt) => {
                      evt.stopPropagation()
                      columnAction?.onClick?.(data, ACTION_TYPE.deposit)
                    }}
                    className="flex-1 capitalize text-xs font-semibold rounded-lg p-2 min-w-[90px] bg-gradient-btn"
                    disabled
                  >
                    {trans.common.deposit}
                  </Button>
                </Stack>
              ) : (
                <Box>
                  <Button
                    data-testid={`${data.symbol}-do-swap-btn`}
                    onClick={(evt) => {
                      evt.stopPropagation()
                      columnAction?.onClick?.(data, ACTION_TYPE.trade)
                    }}
                    className="capitalize bg-gradient-btn text-xs font-semibold rounded-lg p-2 min-w-[150px]"
                  >
                    {trans.core_tokens_list.trade}
                  </Button>
                </Box>
              )}
            </Stack>
          )
        })}
      <Popover
        id={popoverId}
        open={isOpen}
        anchorEl={currentTargetEl}
        onClose={onCloseMoreMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        classes={{
          paper: 'bg-[#141A23] rounded-lg p-2 bg-none shadow-none',
        }}
      >
        <Button
          data-testid="btn-open-chart-mobile"
          onClick={(evt) => {
            evt.stopPropagation()
            onOpenCandleChart(tokenMinForChart)
          }}
          className="text-white py-[10px] px-4 whitespace-nowrap text-sm capitalize"
        >
          {trans.core_tokens_list.open_chart}
        </Button>
      </Popover>

      {tokenInChart && (
        <Drawer
          anchor="bottom"
          open={true}
          onClose={() => setTokenAddressInChart('')}
          sx={{
            '.MuiPaper-elevation': {
              backgroundColor: 'transparent',
              backgroundImage: 'none',
              maxHeight: '50vh',
            },
          }}
          data-testid="top-coins-chart-mobile"
        >
          <CandleChartBox
            tokenIn={tokenInChart.tokenA}
            tokenOut={tokenInChart.tokenB}
            onHideChart={() => setTokenAddressInChart('')}
          />
        </Drawer>
      )}
    </Stack>
  )
}

export default memo(View)
